import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import ReviewSubmitForm from './ReviewSubmitForm';
import ReviewTimeline from './ReviewTimeline';
import { apiService } from '../../utils/api';

/**
 * ReviewsTab - クチコミ表示・投稿メインコンポーネント
 * 
 * Props:
 *   targetId: 企業/投稿ユーザーのUID
 *   targetType: "corporate" | "poster"
 *   currentUserRole: 現在のユーザーロール ("viewer" | "poster" | "corporate" | "admin")
 *   currentUserId: 現在ユーザーのUID (可能なら)
 *   isOwner: 対象ユーザーが自分か
 *   isTaggedCorp: タグ付け企業か（投稿ユーザーの場合）
 */
const ReviewsTab = ({
  targetId,
  targetType,
  currentUserRole,
  currentUserId,
  isOwner,
  isTaggedCorp
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [pendingCount, setPendingCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // クチコミ一覧を取得
  useEffect(() => {
    fetchReviews();
  }, [targetId, targetType, refreshKey]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get(`/api/reviews/${targetId}`, {
        params: {
          target_type: targetType,
          include_pending: isOwner || isTaggedCorp
        }
      });
      // apiService.get はレスポンスボディを直接返す
      const data = response?.data || response || {};
      setReviews(data.reviews || []);
      setPendingCount(data.pending_count || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'クチコミの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmitted = () => {
    setSuccessMessage('クチコミを投稿しました。相手の承認後に公開されます（承認までは「承認待ち」として表示されます）。');
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setSuccessMessage(''), 8000);
  };

  const handleReviewUpdated = () => {
    setSuccessMessage('クチコミを更新しました。');
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleReviewApproved = () => {
    setSuccessMessage('クチコミを承認しました。');
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleReviewDeleted = () => {
    setSuccessMessage('クチコミを削除しました。');
    setRefreshKey(prev => prev + 1);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <Typography variant="h6">クチコミ</Typography>
          {pendingCount > 0 && (
            <Chip
              label={`承認待ち: ${pendingCount}`}
              color="warning"
              size="small"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {reviews.length > 0 ? `${reviews.length}件のクチコミ` : 'クチコミはまだありません'}
        </Typography>
      </Box>

      {/* エラーメッセージ */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* 成功メッセージ */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* 投稿フォーム（viewer ユーザーのみ） */}
      {currentUserRole === 'viewer' && (
        <>
          <ReviewSubmitForm
            targetId={targetId}
            targetType={targetType}
            onSubmitted={handleReviewSubmitted}
          />
          <Divider sx={{ my: 3 }} />
        </>
      )}

      {/* ローディング */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : reviews.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          クチコミがまだありません
        </Typography>
      ) : (
        <ReviewTimeline
          reviews={reviews}
          currentUserId={currentUserId}
          isOwner={isOwner}
          isTaggedCorp={isTaggedCorp}
          onApproved={handleReviewApproved}
          onUpdated={handleReviewUpdated}
          onDeleted={handleReviewDeleted}
          onRefresh={() => setRefreshKey(prev => prev + 1)}
        />
      )}
    </Box>
  );
};

export default ReviewsTab;
